import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Grid, Paper, Stack, TextField, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Chip } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getAllowanceHoursSummary, getAllowanceHoursByWorker, syncAllowanceHours, AllowanceHoursByWorkerRow, getWorkerWorkedHours } from '../services/centralDbClient';
import oidcAuth from '../services/auth/oidc-client';

export default function HoursForAllowancePage() {
  const [fromDate, setFromDate] = useState('2025-01-01');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ from: string; months: string[]; counts: Array<{ period_start: string; count: string }> } | null>(null);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState<null | {
    totalWorkers: number;
    processedWorkers: number;
    totalRows: number;
    processedRows: number;
    created: number;
    updated: number;
  }>(null);
  const [rows, setRows] = useState<AllowanceHoursByWorkerRow[]>([]);
  const [hoursMap, setHoursMap] = useState<Record<string, number>>({});
  const [hoursLoading, setHoursLoading] = useState<Record<string, boolean>>({});

  const monthsCount = useMemo(() => summary?.months?.length ?? 0, [summary]);

  async function refresh() {
    setError(null);
    try {
      const s = await getAllowanceHoursSummary(fromDate ? new Date(fromDate).toISOString() : undefined);
      setSummary(s);
      const list = await getAllowanceHoursByWorker(fromDate ? new Date(fromDate).toISOString() : undefined);
      setRows(list);
      // Reset hours
      setHoursMap({});
      setHoursLoading({});
      // Kick off hours computations in limited parallelism
      void computeAllTotals(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load summary');
    }
  }

  useEffect(() => { refresh(); }, []);

  async function handleSync() {
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress(null);
    try {
      const res = await syncAllowanceHours(fromDate ? new Date(fromDate).toISOString() : undefined);
      setResult(res);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Sync failed');
    } finally {
      setLoading(false);
    }
  }

  async function computeAllTotals(list: AllowanceHoursByWorkerRow[]) {
    const concurrency = 5;
    let index = 0;
    const fromISO = fromDate ? new Date(fromDate).toISOString() : undefined;
    const endISO = new Date().toISOString();

    const runNext = async (): Promise<void> => {
      const i = index++;
      if (i >= list.length) return;
      const item = list[i];
      setHoursLoading(prev => ({ ...prev, [item.workerId]: true }));
      try {
        const data = await getWorkerWorkedHours(item.workerId, { from: fromISO, to: endISO });
        setHoursMap(prev => ({ ...prev, [item.workerId]: Number(data?.hours || 0) }));
      } catch {
        setHoursMap(prev => ({ ...prev, [item.workerId]: 0 }));
      } finally {
        setHoursLoading(prev => ({ ...prev, [item.workerId]: false }));
      }
      await runNext();
    };

    const workers: Promise<void>[] = [];
    for (let k = 0; k < concurrency; k++) workers.push(runNext());
    await Promise.all(workers);
  }

  // Formatting helpers for friendly display
  const formatMonth = (value?: string) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  };
  const formatDateTime = (value?: string) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  };

  async function handleStreamSync() {
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress(null);
    try {
      const token = await oidcAuth.getAccessToken();
      const url = new URL((import.meta as any).env?.VITE_CENTRAL_DB_API_URL || (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000/api/v1');
      url.pathname = url.pathname.replace(/\/$/, '') + '/admin/allowance-hours/sync/stream';
      if (fromDate) {
        url.searchParams.set('from', new Date(fromDate).toISOString());
      }

      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Accept': 'application/x-ndjson',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });

      if (!resp.ok || !resp.body) {
        throw new Error(`Streaming failed (status ${resp.status})`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.phase === 'start' || evt.phase === 'progress') {
              setProgress({
                totalWorkers: Number(evt.totalWorkers || 0),
                processedWorkers: Number(evt.processedWorkers || evt.workerIndex || 0),
                totalRows: Number(evt.totalRows || 0),
                processedRows: Number(evt.processedRows || 0),
                created: Number(evt.created || 0),
                updated: Number(evt.updated || 0),
              });
            } else if (evt.phase === 'done') {
              setProgress({
                totalWorkers: Number(evt.totalWorkers || 0),
                processedWorkers: Number(evt.processedWorkers || 0),
                totalRows: Number(evt.totalRows || 0),
                processedRows: Number(evt.processedRows || 0),
                created: Number(evt.created || 0),
                updated: Number(evt.updated || 0),
              });
              setResult(evt);
            } else if (evt.phase === 'error') {
              setError(evt.error || 'Streaming error');
            }
          } catch {}
        }
      }

      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Streaming sync failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <CalendarMonthIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Hours for Allowance</Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={'auto'}>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleSync}
              disabled={loading}
            >
              {loading ? 'Syncing…' : 'Sync Now'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={'auto'}>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={handleStreamSync}
              disabled={loading}
            >
              {loading ? 'Streaming…' : 'Stream Progress'}
            </Button>
          </Grid>
          <Grid item xs>
            {summary && (
              <Typography variant="body2" color="text.secondary">
                Months in scope: {monthsCount}. Use the button to create/update rows per worker per month.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'error.main' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorOutlineIcon color="error" />
            <Typography color="error">{error}</Typography>
          </Stack>
        </Paper>
      )}

      {result && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Sync Complete</Typography>
          </Stack>
          <Box sx={{ pl: 5 }}>
            <Typography variant="body2">From: {formatDateTime(result.from)}</Typography>
            <Typography variant="body2">Months: {result.months?.length ?? 0}</Typography>
            <Typography variant="body2">Workers: {result.totalWorkers}</Typography>
            <Typography variant="body2">Processed rows: {result.processed}</Typography>
            <Typography variant="body2">Created: {result.created} | Updated: {result.updated}</Typography>
          </Box>
        </Paper>
      )}

      {progress && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Progress</Typography>
          <Typography variant="body2">Workers: {progress.processedWorkers} / {progress.totalWorkers}</Typography>
          <Typography variant="body2">Rows: {progress.processedRows} / {progress.totalRows}</Typography>
          <Typography variant="body2">Created: {progress.created} | Updated: {progress.updated}</Typography>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 0, mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Worker</TableCell>
                <TableCell>Easyflex ID</TableCell>
                <TableCell align="right">Months</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Total Hours Since</TableCell>
                <TableCell align="right">Used in tickets</TableCell>
                <TableCell align="right">Hours left</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const name = `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email || r.workerId;
                const total = hoursMap[r.workerId];
                const isLoading = hoursLoading[r.workerId];
                const used = Number(r.usedHours || 0);
                const left = (total ?? 0) - used;
                return (
                  <TableRow key={r.workerId} hover>
                    <TableCell>{name}</TableCell>
                    <TableCell>{r.easyflexRegistrationNumber ?? '—'}</TableCell>
                    <TableCell align="right">{r.monthsCount}</TableCell>
                    <TableCell>{formatMonth(r.fromPeriod)}</TableCell>
                    <TableCell>{formatMonth(r.toPeriod)}</TableCell>
                    <TableCell>{formatDateTime(r.lastUpdatedAt)}</TableCell>
                    <TableCell align="right">
                      {isLoading ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">loading…</Typography>
                        </Stack>
                      ) : (
                        <Chip label={(total ?? 0).toFixed(2)} color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={used.toFixed(2)} color="default" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={left.toFixed(2)} color={left < 0 ? 'error' : 'success'} size="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell align="center" sx={{ p: 2 }} {...({ colSpan: 9 } as any)}>
                    <Typography variant="body2" color="text.secondary">
                      No rows yet. Run a sync to populate.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}
